import "ol/ol.css";
import "./MapOpenLayer.css";

import { Feature, Map, MapBrowserEvent, Overlay, View } from "ol";
import { defaults as defaultControls } from "ol/control/defaults.js";
import { createEmpty, extend, getCenter } from "ol/extent";
import GeoJSON from "ol/format/GeoJSON.js";
import { Geometry } from "ol/geom";
import TileLayer from "ol/layer/Tile";
import VectorLayer from "ol/layer/Vector.js";
import { useGeographic } from "ol/proj.js";
import VectorSource from "ol/source/Vector.js";
import XYZ from "ol/source/XYZ";
import Fill from "ol/style/Fill";
import Style from "ol/style/Style";
import { ReactElement, useMemo, useRef, useState } from "react";
import { useDeepCompareEffectNoCheck } from "use-deep-compare-effect";

import { Button, Column, FlyoutPanel, Row } from "../../..";
import HelpControl from "./controls";
import { MapOpenLayerGisFileProps } from "./models";

useGeographic();

interface MapOpenLayerMultipleLayersProps {
  centerCoordinate: { lat: number; lng: number };
  gisFiles: MapOpenLayerGisFileProps[];
  drawLayer?: boolean;
  clearDrawBoundaryToggle?: boolean;
  hoverOverlay?: Overlay;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  pointermoveFunc?: (event: MapBrowserEvent<any>) => void;
}

export const MapOpenLayerMultipleLayers = ({
  centerCoordinate,
  gisFiles,
  drawLayer,
  clearDrawBoundaryToggle,
  hoverOverlay,
  pointermoveFunc,
}: MapOpenLayerMultipleLayersProps): JSX.Element => {
  const MAPBOX_TOKEN = "pk.eyJ1Ijoiamlhbm5hbmx1IiwiYSI6ImNtM3BvdjNpdjBlN3oycXM0eXdmcWRxODkifQ._puZrLst8YIgoDn2sye1tQ";
  const mapElement = useRef<HTMLDivElement | null>(null);
  const extentOfFeatures = createEmpty();
  const [displayHelpPanel, setDisplayHelpPanel] = useState(false);

  const satelliteLayer = useMemo(
    () =>
      new TileLayer({
        source: new XYZ({
          url: `https://api.mapbox.com/styles/v1/mapbox/satellite-v9/tiles/{z}/{x}/{y}?access_token=${MAPBOX_TOKEN}`,
          tileSize: 512, // Mapbox uses 512px tiles by default
          maxZoom: 19, // Maximum zoom level for satellite tiles
        }),
      }),
    []
  );

  const shapeLayers = useMemo(() => {
    const tempLayers: VectorLayer<VectorSource<Feature<Geometry>>, Feature<Geometry>>[] = [];

    gisFiles.forEach((gisFile) => {
      if (gisFile.projectShape) {
        const features = gisFile.projectShape.geoJsonContent
          ? new GeoJSON().readFeatures(gisFile.projectShape.geoJsonContent)
          : undefined;
        if (features) {
          features.forEach((feature) => {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            extend(extentOfFeatures, feature.getGeometry()!.getExtent());
          });
          const shapeLayer = new VectorLayer({
            source: new VectorSource({
              features: features || undefined,
              url: gisFile.projectShape.geoJsonFileUrl,
              format: new GeoJSON(),
            }),
            opacity: 0.5,
            style: new Style({
              fill: new Fill({
                color: gisFile.projectShape.fillColor || "#FF865D",
              }),
            }),
          });
          shapeLayer.setProperties({
            projectName: gisFile.extraInfo?.projectName,
            areaNetHa: gisFile.extraInfo?.areaNetHa,
          });
          tempLayers.push(shapeLayer);
        }
      }
    });

    return tempLayers;
  }, [gisFiles, gisFiles[0]?.projectShape?.geoJsonContent, gisFiles[0]?.projectShape?.geoJsonFileUrl]);

  useDeepCompareEffectNoCheck(() => {
    if (!mapElement.current) return undefined;

    const layers = [satelliteLayer, ...shapeLayers];
    const mapObj = new Map({
      layers,
      view: new View({
        center: shapeLayers.length > 0 ? getCenter(extentOfFeatures) : [centerCoordinate.lng, centerCoordinate.lat], // "EPSG:4326" in default
        zoom: 14,
      }),
      target: "map-openlayer-container",
      controls: defaultControls().extend([new HelpControl(setDisplayHelpPanel)]),
    });

    mapObj.setTarget(mapElement.current);

    if (hoverOverlay) mapObj.addOverlay(hoverOverlay);

    mapObj.on("pointermove", (event) => {
      if (pointermoveFunc) pointermoveFunc(event);
    });
    return () => mapObj.setTarget("");
  }, [gisFiles, drawLayer, clearDrawBoundaryToggle]);

  const helpContent = (): ReactElement => (
    <div className="map-help-content">
      <div className="map-help-table">
        <Row>
          <Column span={2}>Tool </Column>
          <Column span={10}>Usage </Column>
        </Row>
        <Row>
          <Column span={2}>
            <div>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 11V6a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v0" />
                <path d="M14 10V4a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v2" />
                <path d="M10 10.5V6a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v8" />
                <path d="M18 11a4 4 0 0 1 4 4v3a8 8 0 0 1-8 8h-4a8 8 0 0 1-8-8v-5a4 4 0 0 1 4-4" />
              </svg>
              Pan map
            </div>
          </Column>
          <Column span={10}>
            <ul>
              <li>Single left click: the map is centred on the clicked point, at the same scale</li>
              <li>Hold down the left mouse button and drag the map canvas</li>
            </ul>
          </Column>
        </Row>
        <Row>
          <Column span={2}>
            <div>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8" />
                <path d="M21 21l-4.35-4.35" />
                <path d="M11 8v6M8 11h6" />
              </svg>
              Zoom in
            </div>
          </Column>
          <Column span={10}>
            <ul>
              <li>Single left click: the map is centred on the clicked point, while the scale gets doubled</li>
              <li>Drag a rectangle on the map canvas with the left mouse button to zoom in to an area.</li>
              <li>Hold the Alt key to switch to the &lsquo;Zoom Out&rsquo; tool</li>
            </ul>
          </Column>
        </Row>
        <Row>
          <Column span={2}>
            <div>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8" />
                <path d="M21 21l-4.35-4.35" />
                <path d="M8 11h6" />
              </svg>
              Zoom out
            </div>
          </Column>
          <Column span={10}>
            <ul>
              <li>Single left click: the map is centred on the clicked point, while the scale gets halved</li>
              <li>Drag a rectangle on the map canvas with the left mouse button to zoom out from an area.</li>
              <li>Hold the Alt key to switch to the &lsquo;Zoom In&rsquo; tool.</li>
            </ul>
          </Column>
        </Row>
        <Row>
          <Column span={2}>Mouse wheel</Column>
          <Column span={10}>
            <ul>
              <li>Pan map: Hold and drag the mouse wheel.</li>
              <li>
                Zoom: Roll the mouse wheel to zoom in or zoom out. With Ctrl key pressed while rolling the mouse wheel
                results in a finer zoom.
              </li>
              <li>Press the back or forward button to browse the map canvas zoom history.</li>
            </ul>
          </Column>
        </Row>
        <Button text="Close" className="btn-close" onClick={() => setDisplayHelpPanel(false)} />
      </div>
    </div>
  );

  return (
    <>
      <div id="map-openlayer-container" className="map-container" ref={mapElement} />
      <FlyoutPanel
        show={displayHelpPanel}
        onClose={() => setDisplayHelpPanel(false)}
        size="Large"
        contentChildren={helpContent()}
        header="Map controls"
        buttonsChildren={undefined}
        height="Full"
      />
    </>
  );
};
